import clamp from 'lodash/clamp';
import moment from 'moment';

import {
  DEFER_VARIANT_DECISION,
  Experiment,
  ExperimentCollectionType,
  ExperimentEndConditionType,
  ExperimentMetric,
  ExperimentStatus,
  ExperimentTestingModel,
} from 'apps/experiments/src/types';
import {Report} from 'common/report/insights/models/report';
import {ChartType, InsightsBookmarkParams} from 'common/types/reports/bookmark';
import {bookmarkUrlsClient} from 'common/report/queries/app-api/bookmark-urls-client';
import {FeatureGate, ReportName, RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import {getReportUrl} from 'common/report/util';
import {FilterClause} from 'common/report/insights/util/types';
import {SharableEntityType} from 'common/report/util/saved-entities/types';
import {FeatureFlagApiResponse} from 'app_api/projects/feature_flags/types';
import {CurrentProject} from 'common/report/model-resources/projects';

import {buildExperimentGroup, buildExperimentTime} from './query-utils';

export const NEW_EXPERIMENT_ID = `new`;

export const DEFAULT_CONTROL_KEY = `Control`;
export const DEFAULT_CONFIDENCE_LEVEL = 0.95;
export const DEFAULT_SAMPLE_SIZE = 10000;
export const DEFAULT_END_AFTER_DAYS_SIZE = 14;
export const DEFAULT_TESTING_MODEL = ExperimentTestingModel.SEQUENTIAL;
export const DEFAULT_END_CONDITION = ExperimentEndConditionType.SAMPLE_SIZE;
export const MIN_CONFIDENCE_LEVEL = 0.8;
export const MAX_CONFIDENCE_LEVEL = 0.9999;
export const MIN_SAMPLE_SIZE = 0;
export const MIN_END_AFTER_DAYS_SIZE = 1;
export const MAX_END_AFTER_DAYS_SIZE = 90;

export const STABLE_OBJ_REF = {};
export const STABLE_ARRAY_REF: unknown[] = [];

export const DATE_FORMAT = `YYYY-MM-DDTHH:mm:ss`;

const MAX_ESTIMATED_COMPLETION_DAYS = 365;

export const NOT_STARTED_STAUSES = new Set([ExperimentStatus.DRAFT, ExperimentStatus.CONFIGURED]);
export const COMPLETED_STATUSES = new Set([
  ExperimentStatus.CONCLUDED,
  ExperimentStatus.SUCCESS,
  ExperimentStatus.FAIL,
  ExperimentStatus.SUCCESS_DEFERRED,
]);

export const DEFAULT_EXPERIMENT: Experiment = {
  id: NEW_EXPERIMENT_ID,
  name: ``,
  creator_id: 0,
  creator_name: ``,
  creator_email: ``,
  metrics: [],
  exposures_cache: {},
  results_cache: {},
  status: ExperimentStatus.DRAFT,
  content_type: SharableEntityType.Experiment,
  settings: {
    collectionMethod: ExperimentCollectionType.EXPOSURE_EVENTS,
    confidenceLevel: DEFAULT_CONFIDENCE_LEVEL,
    sampleSize: DEFAULT_SAMPLE_SIZE,
    endAfterDays: DEFAULT_END_AFTER_DAYS_SIZE,
    testingModel: DEFAULT_TESTING_MODEL,
    endCondition: DEFAULT_END_CONDITION,
  },
  start_date: null,
  end_date: null,
};

export const UPDATEABLE_EXPERIMENT_FIELDS: Array<keyof Experiment> = [
  `name`,
  `description`,
  `hypothesis`,
  `settings`,
  `start_date`,
  `end_date`,
  `metrics`,
  `exposures_cache`,
  `results_cache`,
];

/**
 * Computes the status of an experiment based on its current state and metrics.
 *
 * - If the experiment is in draft and has metrics, it is considered configured.
 * - If the experiment is active, it checks if the completion percentage is 100% to conclude it.
 * - Otherwise, it returns the current status.
 *
 * @param experiment - The experiment object to compute the status for.
 * @returns The computed status of the experiment as an `ExperimentStatus`.
 */
export function computeStatus(experiment?: Experiment) {
  if (!experiment) {
    return ExperimentStatus.DRAFT;
  }
  switch (experiment.status) {
    case ExperimentStatus.DRAFT:
      if (experiment.metrics?.length) {
        return ExperimentStatus.CONFIGURED;
      }
      return ExperimentStatus.DRAFT;
    case ExperimentStatus.ACTIVE:
      if (computeStatusPercent(experiment) >= 1) {
        return ExperimentStatus.CONCLUDED;
      }
      return ExperimentStatus.ACTIVE;
    case ExperimentStatus.SUCCESS:
      return experiment.results_cache?.variant === DEFER_VARIANT_DECISION
        ? ExperimentStatus.SUCCESS_DEFERRED
        : ExperimentStatus.SUCCESS;
    default:
      return experiment.status;
  }
}

/**
 * Computes the completion percentage of an experiment based on its end condition.
 *
 * - For experiments with a sample size end condition, the percentage is calculated as the ratio of exposures to the sample size.
 * - For experiments with a days-based end condition, the percentage is calculated as the elapsed time over the total duration.
 * - Returns `undefined` if the experiment is not active.
 *
 * @param experiment - The experiment object to compute the status percent for.
 * @returns The completion percentage as a number between 0 and 1.
 */
export function computeStatusPercent(experiment: Experiment) {
  if (NOT_STARTED_STAUSES.has(experiment.status)) {
    return 0;
  }
  if (COMPLETED_STATUSES.has(experiment.status)) {
    return 1;
  }

  let percent = 0;
  switch (experiment.settings.endCondition) {
    case ExperimentEndConditionType.SAMPLE_SIZE:
      if (experiment.settings.sampleSize) {
        percent = (experiment?.exposures_cache?.[RESULTS_OVERALL_SUM_KEY] ?? 0) / experiment.settings.sampleSize;
      }
      break;
    case ExperimentEndConditionType.DAYS: {
      if (!experiment.start_date) {
        percent = 0;
      } else {
        const startDate = moment(experiment.start_date);
        const endDate = startDate.clone().add(experiment.settings.endAfterDays, `days`);
        const startMilliseconds = startDate.valueOf();
        const endMilliseconds = endDate.valueOf();
        const currentMilliseconds = moment().valueOf();
        percent = (currentMilliseconds - startMilliseconds) / (endMilliseconds - startMilliseconds);
      }
      break;
    }
  }
  return clamp(percent, 0, 1);
}

export const enum COMPLETION_DATE_STATUS {
  NOT_STARTED = `notStarted`,
  ESTIMATED = `estimated`,
  PENDING_DATA = `pendingData`,
  ACTUAL = `actual`,
}

/**
 * Computes the completion date and status for a given experiment based on its current status and end condition.
 *
 * - If the experiment is in DRAFT status, returns `NOT_STARTED`.
 * - If the experiment is not ACTIVE, returns `ACTUAL` with the experiment's end date.
 * - For ACTIVE experiments:
 *   - If the end condition is SAMPLE_SIZE:
 *     - Returns `NOT_STARTED` if sample size is not set.
 *     - Returns `PENDING_DATA` if progress percent is undefined or zero.
 *     - Returns `ACTUAL` with the current date if percent is 100% or more.
 *     - Otherwise, estimates the completion date based on current progress.
 *     - If the estimated end date exceeds the maximum allowed estimation window, returns `PENDING_DATA`.
 *   - If the end condition is DAYS:
 *     - Returns `NOT_STARTED` if endAfterDays is not set.
 *     - Otherwise, returns `ACTUAL` with the calculated end date.
 *   - For any other end condition, returns `NOT_STARTED`.
 *
 * @param experiment - The experiment object to compute the completion date for.
 * @returns An object containing the completion date status and, if applicable, the estimated or actual completion date.
 */
export function computeCompletionDate(experiment: Experiment): {
  status: COMPLETION_DATE_STATUS;
  date?: Date;
} {
  if (!experiment.start_date) {
    return {
      status: COMPLETION_DATE_STATUS.NOT_STARTED,
    };
  }
  if (experiment.status === ExperimentStatus.DRAFT) {
    return {
      status: COMPLETION_DATE_STATUS.NOT_STARTED,
    };
  }
  if (experiment.status !== ExperimentStatus.ACTIVE) {
    return {
      status: COMPLETION_DATE_STATUS.ACTUAL,
      date: experiment.end_date ? moment(experiment.end_date).toDate() : new Date(),
    };
  }

  switch (experiment.settings.endCondition) {
    case ExperimentEndConditionType.SAMPLE_SIZE: {
      if (!experiment.settings.sampleSize || !experiment.start_date) {
        return {
          status: COMPLETION_DATE_STATUS.NOT_STARTED,
        };
      }
      const percent = computeStatusPercent(experiment);
      if (percent === undefined || percent === 0) {
        return {
          status: COMPLETION_DATE_STATUS.PENDING_DATA,
        };
      }
      if (percent >= 1) {
        return {
          status: COMPLETION_DATE_STATUS.ACTUAL,
          date: new Date(),
        };
      }
      const startDate = moment(experiment.start_date);
      const today = moment();
      const currentMilliseconds = today.valueOf() - startDate.valueOf();
      // require more than an hr of data to estimate
      if (currentMilliseconds <= 1000 * 3600) {
        return {
          status: COMPLETION_DATE_STATUS.PENDING_DATA,
        };
      }
      const estimatedMilliseconds = currentMilliseconds / percent;
      const estimatedEndDate = moment(startDate.valueOf() + estimatedMilliseconds);
      if (estimatedEndDate.isAfter(startDate.clone().add(MAX_ESTIMATED_COMPLETION_DAYS, `days`))) {
        return {
          status: COMPLETION_DATE_STATUS.PENDING_DATA,
        };
      }
      return {
        status: COMPLETION_DATE_STATUS.ESTIMATED,
        date: estimatedEndDate.toDate(),
      };
    }
    case ExperimentEndConditionType.DAYS: {
      if (!experiment.settings.endAfterDays || !experiment.start_date) {
        return {
          status: COMPLETION_DATE_STATUS.NOT_STARTED,
        };
      }
      const endDate = moment(experiment.start_date).add(experiment.settings.endAfterDays, `days`);
      return {
        status: COMPLETION_DATE_STATUS.ACTUAL,
        date: endDate.toDate(),
      };
    }
    default:
      return {
        status: COMPLETION_DATE_STATUS.NOT_STARTED,
      };
  }
}

/**
 * Converts an `ExperimentMetric` object into `InsightsBookmarkParams` for use in insights bookmarks.
 *
 * @param params - The parameters for conversion.
 * @param params.experimentMetric - The experiment metric to convert.
 * @param params.overrideChartType - Optional chart type to override the default.
 * @param params.additionalFilters - Optional array of additional filter clauses to apply.
 * @returns The constructed `InsightsBookmarkParams` object based on the provided experiment metric and options.
 */
export function convertExperimentMetricToInsightsBookmarkParams({
  experimentMetric,
  overrideChartType,
  additionalFilters = [],
}: {
  experimentMetric?: ExperimentMetric;
  overrideChartType?: ChartType;
  additionalFilters?: FilterClause[];
}): InsightsBookmarkParams {
  const report = Report.createDefaultReport();

  if (overrideChartType) {
    report.overrideChartType(overrideChartType);
  }

  const bookmarkParams = report.toBookmarkData();
  if (experimentMetric) {
    bookmarkParams.sections.globalDataGroupId = experimentMetric.dataGroupId;
    bookmarkParams.sections.show = experimentMetric.definition ? [experimentMetric.definition] : [];
    bookmarkParams.sections.filter = [...(experimentMetric.filters ?? []), ...additionalFilters];
  }

  return bookmarkParams;
}

/**
 * Converts an experiment metric into an Insights report URL.
 *
 * This function generates bookmark parameters for the given experiment metric,
 * applies additional filters if provided, and sets up grouping and time sections
 * based on the experiment. It then retrieves a bookmark URL slug and constructs
 * the final Insights report URL for the specified project and workspace.
 *
 * @param params - The parameters for conversion.
 * @param params.experiment - The experiment object to use for grouping and time sections.
 * @param params.experimentMetric - The experiment metric to convert.
 * @param params.projectId - The ID of the project.
 * @param params.workspaceId - The ID of the workspace.
 * @param params.additionalFilters - Optional additional filter clauses to apply.
 * @param params.featureFlag - Feature flag associated with the experiment. This is not optional,
 *  you MUST provide it if the experiment is configured with a feature flag.
 * @returns A promise that resolves to the Insights report URL as a string.
 */
export async function convertExperimentMetricToInsightUrl({
  experiment,
  experimentMetric,
  projectId,
  workspaceId,
  additionalFilters,
  featureFlag,
}: {
  experiment: Experiment;
  experimentMetric: ExperimentMetric;
  projectId: number;
  workspaceId: number;
  additionalFilters?: FilterClause[];
  featureFlag?: FeatureFlagApiResponse;
}) {
  const bookmarkParams = convertExperimentMetricToInsightsBookmarkParams({
    experimentMetric,
    overrideChartType: ChartType.Line,
    additionalFilters,
  });
  bookmarkParams.sections.group = buildExperimentGroup(experiment, featureFlag);
  bookmarkParams.sections.time = buildExperimentTime(experiment);

  const bookmarkUrlSlug = await bookmarkUrlsClient.getBookmarkUrlAsync({
    project_id: projectId,
    type: ReportName.Insights,
    params: bookmarkParams,
  });

  return getReportUrl(`insights#${bookmarkUrlSlug}`, {
    projectId,
    workspaceId,
  });
}

export function experimentStartDateUsingProjectGates({
  startDate,
  currentProject,
}: {
  startDate: string;
  currentProject?: CurrentProject;
}): string {
  if (!startDate || !currentProject) {
    return startDate;
  }
  const minStartDate = currentProject?.getFeatureGate(FeatureGate.ExperimentsStartDate);
  if (minStartDate) {
    const minStart = moment(minStartDate);
    const eventStart = moment(startDate);
    if (eventStart.isBefore(minStart)) {
      return minStart.format(DATE_FORMAT);
    }
    return eventStart.format(DATE_FORMAT);
  }
  return moment(startDate).format(DATE_FORMAT);
}
